import './NewsWall.scss';
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useProtectedInterval } from '../../hooks/useProtectedInterval';
import { setUnreadNewsCount } from '../../storeRedux';
import store from '../../storeRedux';
import Pagination from '../../components/Layout/Pagination';
import EmptyState from '../../components/Layout/EmptyState';
import Toast from '../../components/Toast/Toast';

export default function NewsWall() {
    const { t } = useTranslation('news');
    const theme = useSelector((state) => state.APP_THEME);
    const api_url = useSelector((state) => state.API_URL);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [newsData, setNewsData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [pageIndex, setPageIndex] = useState(1);
    const [filter, setFilter] = useState('all');
    const [selectedNews, setSelectedNews] = useState(null);
    const [error, setError] = useState(false);

    const fetchNews = useCallback(async () => {
        const jwtToken = localStorage.getItem('accessToken');
        try {
            const unreadParam = filter === 'unread' ? '&unread=true' : '';
            const response = await fetch(
                api_url + `news/all/?page=${pageIndex}&page_size=${pageSize}${unreadParam}`,
                {
                    headers: {
                        'Authorization': `JWT ${jwtToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.clear();
                    navigate('/auth');
                }
                return;
            }
            const data = await response.json();
            setNewsData(data.results || []);
            setTotalCount(data.count || 0);
            dispatch(setUnreadNewsCount(data.unread_count || 0));
        } catch {
            setError(true);
        }
    }, [api_url, pageIndex, pageSize, filter, navigate, dispatch]);

    useProtectedInterval(fetchNews, [pageSize, pageIndex, filter]);

    const markAsRead = async (newsId) => {
        const jwtToken = localStorage.getItem('accessToken');
        try {
            const response = await fetch(api_url + `news/${newsId}/read/`, {
                method: 'POST',
                headers: {
                    'Authorization': `JWT ${jwtToken}`,
                    'Content-Type': 'application/json',
                },
            });
            if (response.ok) {
                setNewsData((prev) =>
                    prev.map((n) => (n.id === newsId ? { ...n, is_read: true } : n))
                );
                setSelectedNews((prev) =>
                    prev && prev.id === newsId ? { ...prev, is_read: true } : prev
                );
                const currentCount = store.getState().UNREAD_NEWS_COUNT;
                dispatch(setUnreadNewsCount(Math.max(0, currentCount - 1)));
            }
        } catch {}
    };

    const handleFilterChange = (newFilter) => {
        setFilter(newFilter);
        setPageIndex(1);
    };

    return (
        <div className={theme === 'light' ? 'NewsWall' : 'NewsWall dark'}>
            <Toast
                show={error}
                type="error"
                message={t('common:error')}
                onClose={() => setError(false)}
            />
            <div className="content">
                <div className="NewsWall__toolbar">
                    <button
                        className={`NewsWall__filter-btn ${filter === 'all' ? 'active' : ''}`}
                        onClick={() => handleFilterChange('all')}
                    >
                        {t('allNews')}
                    </button>
                    <button
                        className={`NewsWall__filter-btn ${filter === 'unread' ? 'active' : ''}`}
                        onClick={() => handleFilterChange('unread')}
                    >
                        {t('unreadOnly')}
                    </button>
                </div>

                <Pagination
                    pageIndex={pageIndex}
                    totalCount={totalCount}
                    pageSize={pageSize}
                    onPageChange={setPageIndex}
                    onPageSizeChange={setPageSize}
                    pageSizes={[5, 10, 15]}
                />

                {newsData.length === 0 ? (
                    <EmptyState
                        title={t('emptyTitle')}
                        description={t('emptyDescription')}
                    />
                ) : (
                    <div className="NewsWall__grid">
                        {newsData.map((item) => (
                            <div
                                key={item.id}
                                className={`news-card ${!item.is_read ? 'news-card--unread' : ''}`}
                                onClick={() => setSelectedNews(item)}
                            >
                                <div className="news-card__header">
                                    <span className="news-card__date">{item.created}</span>
                                    {!item.is_read && (
                                        <span className="news-card__badge">{t('unreadStatus')}</span>
                                    )}
                                </div>
                                <h3 className="news-card__title">{item.title}</h3>
                                <p className="news-card__content">{item.content}</p>
                                <span className="news-card__read-more">{t('readMore')}</span>
                            </div>
                        ))}
                    </div>
                )}

                <Pagination
                    pageIndex={pageIndex}
                    totalCount={totalCount}
                    pageSize={pageSize}
                    onPageChange={setPageIndex}
                    onPageSizeChange={setPageSize}
                    pageSizes={[5, 10, 15]}
                />
            </div>

            {selectedNews && (
                <div className="news-detail-overlay" onClick={() => setSelectedNews(null)}>
                    <div className="news-detail-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="news-detail-modal__header">
                            <h3 className="news-detail-modal__title">{selectedNews.title}</h3>
                            <button
                                className="news-detail-modal__close"
                                onClick={() => setSelectedNews(null)}
                            >
                                &times;
                            </button>
                        </div>
                        <div className="news-detail-modal__body">
                            <p className="news-detail-modal__content">{selectedNews.content}</p>
                            <span className="news-detail-modal__date">
                                {t('published')}: {selectedNews.created}
                            </span>
                        </div>
                        {!selectedNews.is_read && (
                            <div className="news-detail-modal__footer">
                                <button
                                    className="news-detail-modal__btn"
                                    onClick={() => markAsRead(selectedNews.id)}
                                >
                                    {t('markAsRead')}
                                </button>
                            </div>
                        )}
                        {selectedNews.is_read && (
                            <div className="news-detail-modal__footer">
                                <span className="news-detail-modal__read-badge">
                                    {t('readStatus')}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
